/**
 * Save format dispatch + shape validation.
 *
 * The save format is currently a single object with `version: 4` and a set of
 * subsystem blobs (upgrades, stats, trader, buildings, money, exploration,
 * persistentStats, achievements).
 *
 * This module centralizes the two things `load()` should do before touching
 * subsystems:
 *
 *   1. Confirm the envelope is an object with a known `version`.
 *   2. Dispatch to a per-version validator that narrows the shape.
 *
 * Per-subsystem deserializers still own their own field-level migrations.
 *
 * v3 — Item model replaced discrete quality tiers (basic/uncommon/rare/
 * legendary) with a continuous 1..100 score + generated name. Items from v2
 * cannot be translated directly, so any older save is discarded.
 *
 * v4 — Added persistentStats + achievements blobs backing the achievement
 * system. Migration is purely additive: v3 saves load cleanly and simply get
 * default (zeroed) PersistentStats on first load.
 */

/** Current save schema version. Bump when adding cross-subsystem changes. */
export const CURRENT_SAVE_VERSION = 4;

/** Shape of a v4 save envelope. Subsystem blobs are `unknown` — each
 *  subsystem's own deserializer validates its own shape. */
export interface SaveV4 {
  version: 4;
  money: unknown;
  upgrades?: unknown;
  stats?: unknown;
  trader?: unknown;
  buildings?: unknown;
  exploration?: unknown;
  persistentStats?: unknown;
  achievements?: unknown;
}

export type CurrentSave = SaveV4;

/**
 * Validate and dispatch a raw JSON-parsed value to a known save envelope.
 * Returns `null` if the value isn't an object, lacks a version, or has a
 * version we don't know how to load. The caller is expected to reset to
 * defaults on `null`.
 */
export function migrate(raw: unknown): CurrentSave | null {
  if (!raw || typeof raw !== 'object') return null;
  const env = raw as { version?: unknown };
  const version = typeof env.version === 'number' ? env.version : null;

  switch (version) {
    case 4:
      return validateV4(raw as Record<string, unknown>);
    case 3:
      return migrateV3toV4(raw as Record<string, unknown>);
    // v1 + v2 used discrete item quality and are intentionally dropped.
    default:
      return null;
  }
}

function validateV4(raw: Record<string, unknown>): SaveV4 | null {
  const money = typeof raw.money === 'number' && Number.isFinite(raw.money) ? raw.money : 0;
  return {
    version: 4,
    money,
    upgrades:        raw.upgrades,
    stats:           raw.stats,
    trader:          raw.trader,
    buildings:       raw.buildings,
    exploration:     raw.exploration,
    persistentStats: raw.persistentStats,
    achievements:    raw.achievements,
  };
}

/** Additive migration: v3 saves load cleanly; persistentStats + achievements
 *  default to undefined, and their deserializers will seed fresh defaults. */
function migrateV3toV4(raw: Record<string, unknown>): SaveV4 | null {
  const money = typeof raw.money === 'number' && Number.isFinite(raw.money) ? raw.money : 0;
  return {
    version: 4,
    money,
    upgrades:        raw.upgrades,
    stats:           raw.stats,
    trader:          raw.trader,
    buildings:       raw.buildings,
    exploration:     raw.exploration,
    persistentStats: undefined,
    achievements:    undefined,
  };
}
